import { Search } from 'lucide-react';
import { Contractor } from '../../types';

interface ContractFiltersProps {
    isSuperAdmin: boolean;
    contractors: Contractor[];
    selectedContractor: string;
    setSelectedContractor: (id: string) => void;
    keyword: string;
    setKeyword: (keyword: string) => void;
    naicsCode: string;
    setNaicsCode: (code: string) => void;
    setAside: string;
    setSetAside: (aside: string) => void;
    zipCode: string;
    setZipCode: (zip: string) => void;
    radius: string;
    setRadius: (radius: string) => void;
    city: string;
    setCity: (city: string) => void;
    stateCode: string;
    setStateCode: (state: string) => void;
    limit: string;
    setLimit: (limit: string) => void;
    loading: boolean;
    handleSearch: () => void;
    isMobileFiltersOpen: boolean;
    setAsideOptions: { value: string; label: string }[];
}

export default function ContractFilters({
    isSuperAdmin,
    contractors,
    selectedContractor,
    setSelectedContractor,
    keyword,
    setKeyword,
    naicsCode,
    setNaicsCode,
    setAside,
    setSetAside,
    zipCode,
    setZipCode,
    radius,
    setRadius,
    city,
    setCity,
    stateCode,
    setStateCode,
    limit,
    setLimit,
    loading,
    handleSearch,
    isMobileFiltersOpen,
    setAsideOptions
}: ContractFiltersProps) {
    return (
        <div className={`xl:col-span-3 space-y-6 h-fit xl:sticky xl:top-6 ${isMobileFiltersOpen ? 'block' : 'hidden md:hidden xl:block'}`}>
            {/* Workspace Context */}
            {isSuperAdmin && (
                <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4">
                    <label className="text-[10px] text-gray-500 font-bold uppercase tracking-wider mb-2 block">Workspace</label>
                    <div className="relative">
                        <select
                            value={selectedContractor}
                            onChange={(e) => setSelectedContractor(e.target.value)}
                            className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-lg focus:ring-blue-800 focus:border-blue-700 block p-2.5 cursor-pointer hover:bg-white/10 transition-colors"
                        >
                            <option value="" disabled>Select Profile...</option>
                            {contractors.map((contractor) => (
                                <option key={contractor.id} value={contractor.id} className="bg-slate-900">
                                    {contractor.displayName}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}

            {/* Search Filters */}
            <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4">
                <h3 className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-4">Refine Feed</h3>
                <div className="space-y-4">
                    <div>
                        <label className="text-xs text-gray-500 mb-1.5 block">Keywords</label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-600" />
                            <input
                                type="text"
                                value={keyword}
                                onChange={(e) => setKeyword(e.target.value)}
                                className="w-full bg-white/5 border border-white/10 rounded-lg py-2 pl-9 pr-3 text-xs text-white placeholder-gray-600 focus:outline-none focus:border-blue-800 focus:ring-1 focus:ring-blue-800 transition-all"
                                placeholder="e.g. Cyber, Cloud..."
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="text-xs text-gray-500 mb-1.5 block">NAICS</label>
                            <div className="relative">
                                <input
                                    type="text"
                                    value={naicsCode}
                                    onChange={(e) => setNaicsCode(e.target.value)}
                                    className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-xs text-white focus:outline-none focus:border-blue-800 transition-all"
                                    placeholder="541512"
                                />
                                <a href="https://www.naics.com/search/" target="_blank" rel="noopener noreferrer" className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-blue-400 hover:text-blue-300 font-medium bg-blue-500/10 px-1.5 py-0.5 rounded cursor-pointer transition-colors">
                                    Lookup
                                </a>
                            </div>
                            <div className="mt-2 flex flex-wrap gap-1.5">
                                {['541511', '541512', '541330', '518210', '541611'].map(code => (
                                    <button
                                        key={code}
                                        onClick={() => setNaicsCode(code)}
                                        className="text-[10px] bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white px-2 py-1 rounded border border-white/5 transition-colors"
                                        title="Click to apply"
                                    >
                                        {code}
                                    </button>
                                ))}
                                <span className="text-[10px] text-gray-600 self-center pl-1">Popular</span>
                            </div>
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 mb-1.5 block">Type</label>
                            <select
                                value={setAside}
                                onChange={(e) => setSetAside(e.target.value)}
                                className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-2 text-xs text-white focus:outline-none focus:border-blue-800 transition-all"
                            >
                                <option value="">Any</option>
                                {setAsideOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div>
                        <label className="text-xs text-gray-500 mb-1.5 block">Max Results (Scan Limit)</label>
                        <select
                            value={limit}
                            onChange={(e) => setLimit(e.target.value)}
                            className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-xs text-white focus:outline-none focus:border-blue-800 transition-all"
                        >
                            <option value="10">10 Contracts (Fastest)</option>
                            <option value="25">25 Contracts (Recommended)</option>
                            <option value="50">50 Contracts (Deep Scan)</option>
                            <option value="100">100 Contracts (Slow)</option>
                        </select>
                    </div>

                    <div className="pt-4 border-t border-white/5 space-y-4">
                        <h4 className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">Work Location</h4>

                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="text-xs text-gray-500 mb-1.5 block">Zip Code</label>
                                <input
                                    type="text"
                                    value={zipCode}
                                    onChange={(e) => setZipCode(e.target.value)}
                                    className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-xs text-white focus:outline-none focus:border-blue-800 transition-all font-mono"
                                    placeholder="20001"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-gray-500 mb-1.5 block">Radius</label>
                                <select
                                    value={radius}
                                    onChange={(e) => setRadius(e.target.value)}
                                    className="w-full bg-white/5 border border-white/10 rounded-lg py-1.5 px-2 text-[11px] text-white focus:outline-none focus:border-blue-800 transition-all"
                                >
                                    <option value="25">25 miles</option>
                                    <option value="50">50 miles</option>
                                    <option value="100">100 miles</option>
                                    <option value="250">250 miles</option>
                                    <option value="all">Any Dist</option>
                                </select>
                            </div>
                        </div>

                        <div className="grid grid-cols-3 gap-2">
                            <div className="col-span-2">
                                <label className="text-xs text-gray-500 mb-1.5 block">City</label>
                                <input
                                    type="text"
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                    className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-xs text-white focus:outline-none focus:border-blue-800 transition-all"
                                    placeholder="Washington"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-gray-500 mb-1.5 block">State</label>
                                <input
                                    type="text"
                                    value={stateCode}
                                    onChange={(e) => setStateCode(e.target.value.toUpperCase())}
                                    maxLength={2}
                                    className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-xs text-white focus:outline-none focus:border-blue-800 transition-all font-mono"
                                    placeholder="DC"
                                />
                            </div>
                        </div>
                    </div>

                    <button onClick={handleSearch} disabled={loading} className="w-full py-2.5 bg-blue-600 hover:bg-blue-500 border border-transparent rounded-lg text-xs font-bold text-white shadow-lg shadow-blue-900/20 transition-all flex items-center justify-center gap-2">
                        {loading ? 'Scanning...' : <><Search className="w-3.5 h-3.5" /> Find Matches</>}
                    </button>
                </div>
            </div>
        </div>
    );
}
